import type { SurveyLocalizedData } from '@/shared/api/types/survey-types'
import {
  normalizeLocalizedDataMap,
  normalizeLocalizedLanguageKey,
} from '@/shared/api/mappers/localized-data-mapper'
import {
  mergeTranslationsForLanguages,
  resolveDefaultLanguages,
} from '@/shared/utils/language-utils'

export type SurveyLocalizedTextField = 'name' | 'description'
type SurveyLocalizedPrimaryField = 'name' | 'title' | 'question'

/** Giống POI/Tour: chỉ ngôn ngữ tenant đang active, sortOrder. */
export function surveyLanguagesFromTenantLanguages(
  items: { languageCode: string; isActive?: boolean; sortOrder?: number }[],
): string[] {
  return resolveDefaultLanguages(
    items.map((item) => ({
      languageCode: item.languageCode,
      isActive: item.isActive !== false,
      sortOrder: item.sortOrder ?? 0,
    })),
  )
}

function pickSurveyLocalizedEntry(
  entry: Record<string, string> | undefined,
  primaryField: SurveyLocalizedPrimaryField = 'name',
): Record<SurveyLocalizedTextField, string> {
  if (!entry) {
    return { name: '', description: '' }
  }
  // Survey version API uses title; survey question API uses question.
  const name =
    primaryField === 'question'
      ? (entry['question'] ?? entry['name'] ?? entry['title'] ?? '')
      : primaryField === 'title'
        ? (entry['title'] ?? entry['name'] ?? entry['question'] ?? '')
        : (entry['name'] ?? entry['title'] ?? entry['question'] ?? '')
  const description = entry['description'] ?? ''
  return { name: String(name), description: String(description) }
}

/**
 * Thứ tự tab ngôn ngữ: tenant trước, sau đó mọi key còn lại từ API (đã chuẩn hóa key),
 * giống tinh thần POI (mọi locale trong localizedData đều có mặt).
 */
export function surveyEditorLanguageCodes(
  tenantLanguageCodes: string[],
  localizedData: SurveyLocalizedData | undefined,
): string[] {
  const normMap = localizedData ? normalizeLocalizedDataMap(localizedData) : {}
  const seenNorm = new Set<string>()
  const ordered: string[] = []

  for (const raw of tenantLanguageCodes) {
    const n = normalizeLocalizedLanguageKey(raw)
    if (seenNorm.has(n)) continue
    seenNorm.add(n)
    ordered.push(raw)
  }

  for (const n of Object.keys(normMap).sort((a, b) => a.localeCompare(b))) {
    if (!seenNorm.has(n)) {
      seenNorm.add(n)
      ordered.push(n)
    }
  }

  return ordered
}

export function localizedDataToValuesByLanguage(
  localizedData: SurveyLocalizedData | undefined,
  tenantLanguages: string[],
  options?: { primaryField?: SurveyLocalizedPrimaryField },
): Record<string, Record<SurveyLocalizedTextField, string>> {
  const normMap = localizedData ? normalizeLocalizedDataMap(localizedData) : {}
  const langs = surveyEditorLanguageCodes(tenantLanguages, localizedData)
  const result: Record<string, Record<SurveyLocalizedTextField, string>> = {}
  for (const lang of langs) {
    const n = normalizeLocalizedLanguageKey(lang)
    result[lang] = pickSurveyLocalizedEntry(normMap[n], options?.primaryField)
  }
  return result
}

export function valuesByLanguageToLocalizedData(
  values: Record<string, Record<SurveyLocalizedTextField, string>>,
  options?: { primaryField?: SurveyLocalizedPrimaryField },
): SurveyLocalizedData {
  const out: SurveyLocalizedData = {}
  const primaryField = options?.primaryField ?? 'name'
  for (const [lang, fields] of Object.entries(values)) {
    const trimmedName = fields.name?.trim() ?? ''
    const trimmedDescription = fields.description?.trim() ?? ''
    if (!trimmedName && !trimmedDescription) continue
    out[lang] = {
      ...(trimmedName ? { [primaryField]: trimmedName } : {}),
      ...(trimmedDescription ? { description: trimmedDescription } : {}),
    }
  }
  return out
}

/** Dùng khi hydrate giống Tour: mảng dòng dịch → payload API `localizedData`. */
export function surveyTranslationsToLocalizedData(
  rows: Array<{ language: string; name: string; description: string }>,
  options?: { primaryField?: SurveyLocalizedPrimaryField },
): SurveyLocalizedData {
  return valuesByLanguageToLocalizedData(
    Object.fromEntries(rows.map((r) => [r.language, { name: r.name, description: r.description }])),
    options,
  )
}

/** Tạo đủ row theo danh sách ngôn ngữ, giữ lại dữ liệu người dùng đã nhập nếu có. */
export function mergeSurveyTranslationRows(
  languages: string[],
  rows: Array<{ language: string; name: string; description: string }> | undefined,
) {
  return mergeTranslationsForLanguages(languages, rows ?? [], (language) => ({
    language,
    name: '',
    description: '',
  }))
}
